import { createHash } from "crypto";
import { sql } from "drizzle-orm";
import { decrypt, encrypt } from "./crypto.js";
export { OAUTH_EXCHANGE_SYSTEM_SCHEMA_STATEMENTS } from "./auth-oauth-exchange-schema.js";

export type OAuthExchangeRecord = {
  sessionToken: string;
  user: { id: string; email: string; name?: string | null };
};

type ExecuteDb = {
  execute: (query: unknown) => Promise<unknown>;
};

type OAuthExchangeRow = {
  session_token_encrypted?: string;
  token_iv?: string;
  user_id?: string;
  user_email?: string;
  user_name?: string | null;
};

export const OAUTH_EXCHANGE_CODE_TTL_MS = 5 * 60 * 1000;

function rowsFromResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

function hashOAuthExchangeCode(code: string): string {
  return createHash("sha256").update(code).digest("hex");
}

export async function createOAuthExchangeCode(db: ExecuteDb, record: OAuthExchangeRecord): Promise<string> {
  const code = crypto.randomUUID();
  const codeHash = hashOAuthExchangeCode(code);
  const encrypted = encrypt(record.sessionToken);
  const expiresAt = new Date(Date.now() + OAUTH_EXCHANGE_CODE_TTL_MS);

  await db.execute(sql`DELETE FROM _gencow_oauth_codes WHERE expires_at <= NOW() OR used_at IS NOT NULL`);
  await db.execute(sql`
    INSERT INTO _gencow_oauth_codes (
      code_hash,
      session_token_encrypted,
      token_iv,
      user_id,
      user_email,
      user_name,
      expires_at
    )
    VALUES (
      ${codeHash},
      ${encrypted.encrypted},
      ${encrypted.iv},
      ${record.user.id},
      ${record.user.email},
      ${record.user.name ?? null},
      ${expiresAt}
    )
  `);

  return code;
}

export async function consumeOAuthExchangeCode(
  db: ExecuteDb,
  code: string,
): Promise<OAuthExchangeRecord | null> {
  const codeHash = hashOAuthExchangeCode(code);
  const result = await db.execute(sql`
    UPDATE _gencow_oauth_codes
    SET used_at = NOW()
    WHERE code_hash = ${codeHash}
      AND used_at IS NULL
      AND expires_at > NOW()
    RETURNING session_token_encrypted, token_iv, user_id, user_email, user_name
  `);

  const row = rowsFromResult<OAuthExchangeRow>(result)[0];
  if (!row?.session_token_encrypted || !row.token_iv || !row.user_id || !row.user_email) {
    return null;
  }

  return {
    sessionToken: decrypt(row.session_token_encrypted, row.token_iv),
    user: {
      id: row.user_id,
      email: row.user_email,
      name: row.user_name ?? undefined,
    },
  };
}
